import './SearchField.scss';
import cn from 'classnames';
import {
  ChangeEventHandler,
  InputHTMLAttributes,
  ReactElement,
  Ref,
  useCallback,
  useId,
  useRef
} from 'react';
import { Form } from 'react-bootstrap';
import { MdClose } from 'react-icons/md';
import {
  Appearance,
  Background,
  BackgroundType,
  Design,
  DesignType,
  Size,
  SizeType
} from '../designConstants';
import { IconButton } from '../IconButton';
import { useToolbarSize } from '../Toolbar';
import SearchIcon from './SearchIcon.svg?react';

interface SearchFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  value: string;
  onChange: ChangeEventHandler<HTMLInputElement>;
  id?: string;
  background?: BackgroundType;
  design?: DesignType;
  size?: SizeType;
  label?: string;
  leadingIcon?: ReactElement | null;
  disableClear?: boolean;
  ref?: Ref<HTMLInputElement>;
}

export const SearchField = ({
  id,
  onChange,
  value,
  background = Background.LIGHT,
  design = Design.BRAND,
  size: sizeProp,
  label,
  leadingIcon,
  disableClear = false,
  placeholder,
  disabled = false,
  ref,
  className,
  ...inputPassthroughProps
}: SearchFieldProps) => {
  const size = useToolbarSize(sizeProp);
  const autoId = useId();
  const resolvedId = id ?? autoId;
  const icon = leadingIcon === undefined ? <SearchIcon className='search-icon' /> : leadingIcon;
  const showClear = !disableClear && !disabled && value !== '';

  // Internal ref for the clear-button flow; merged with any caller-provided
  // ref below so both point at the same underlying <input>.
  const localRef = useRef<HTMLInputElement>(null);
  // Alias so the ref-object write below isn't flagged as mutating a
  // function-parameter — the ref-as-prop pattern requires assigning to
  // `.current`, but the linter only tracks the parameter itself.
  const externalRef = ref;
  const mergedRef = useCallback(
    (node: HTMLInputElement | null) => {
      localRef.current = node;
      if (typeof externalRef === 'function') {
        externalRef(node);
      } else if (externalRef) {
        externalRef.current = node;
      }
    },
    [externalRef]
  );

  const handleClearClick = () => {
    const input = localRef.current;
    if (!input) return;
    // Bypass React's controlled-input value cache (the property setter is
    // wrapped internally to track the "last committed value") by calling the
    // native HTMLInputElement.value setter directly, then dispatch a real
    // native 'input' event. React re-reads the current value, sees a change,
    // and fires its own onChange with a proper SyntheticEvent — no cast.
    const nativeSetter = Object.getOwnPropertyDescriptor(
      window.HTMLInputElement.prototype,
      'value'
    )?.set;
    nativeSetter?.call(input, '');
    input.dispatchEvent(new Event('input', { bubbles: true }));
  };

  return (
    <div
      className={cn('bpm-search-field', design, background, size, className, {
        'has-value': value !== ''
      })}
    >
      {label && <Form.Label htmlFor={resolvedId}>{label}</Form.Label>}
      {icon != null && <div className='search-icon-container'>{icon}</div>}
      <Form.Control
        {...inputPassthroughProps}
        ref={mergedRef}
        className='search-input'
        id={resolvedId}
        placeholder={placeholder}
        disabled={disabled}
        onChange={onChange}
        value={value}
      />
      {showClear && (
        <IconButton
          className='search-clear'
          appearance={Appearance.EMPTY}
          design={Design.NEUTRAL}
          size={Size.SM}
          aria-label='Clear'
          onClick={handleClearClick}
        >
          <MdClose />
        </IconButton>
      )}
    </div>
  );
};
